import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Users, Search, X, Shield, ChevronRight, Loader2,
  Sparkles, Star, Swords, Hash, Clock, RefreshCw, CheckCircle
} from 'lucide-react';

interface MemberRole { name: string; color: string | null; }
interface MemberPokemon { collectionCount: number; shinyHunt: string | null; }
interface MemberLevel { level: number; xp: number; }

interface Member {
  id: string;
  username: string;
  globalName: string | null;
  displayName: string;
  avatar: string;
  joinedAt: string | null;
  isOwner: boolean;
  roles: MemberRole[];
  pokemon: MemberPokemon;
  level: MemberLevel | null;
}

interface RolesPanelProps {
  guildData: any;
  guild: any;
}

function relTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 1) return 'Today';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function Avatar({ src, name, size = 40 }: { src: string; name: string; size?: number }) {
  const [err, setErr] = useState(false);
  if (err) {
    return (
      <div
        className="rounded-full bg-gradient-to-br from-primary/40 to-secondary/40 flex items-center justify-center text-white font-bold shrink-0"
        style={{ width: size, height: size, fontSize: size * 0.38 }}
      >
        {name.charAt(0).toUpperCase()}
      </div>
    );
  }
  return (
    <img
      src={src} alt={name} onError={() => setErr(true)}
      className="rounded-full object-cover shrink-0"
      style={{ width: size, height: size }}
    />
  );
}

function RoleBadge({ role }: { role: MemberRole }) {
  const color = role.color || '#5865f2';
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border"
      style={{ color, borderColor: color + '40', backgroundColor: color + '15' }}
    >
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
      {role.name}
    </span>
  );
}

/* ─── Member detail slider panel ─────────────────────────────────────────── */
function MemberDetail({
  member, guildData, token, guildId, onClose, onRoleChange
}: {
  member: Member;
  guildData: any;
  token: string;
  guildId: string;
  onClose: () => void;
  onRoleChange: () => void;
}) {
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);
  const [roleFlash, setRoleFlash] = useState<{ id: string; ok: boolean } | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  const allRoles: MemberRole[] = (guildData?.roles || []).filter((r: any) => r.name !== '@everyone');
  const memberRoleNames = new Set(member.roles.map(r => r.name));

  async function toggleRole(role: any) {
    const hasRole = memberRoleNames.has(role.name);
    setUpdatingRole(role.id);
    setRoleError(null);
    try {
      const res = await fetch(
        `${(import.meta.env.VITE_API_URL || '')}/api/dashboard/${guildId}/members/${member.id}/roles`,
        {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ roleId: role.id, action: hasRole ? 'remove' : 'add' }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg = data?.error || `HTTP ${res.status}`;
        setRoleError(msg);
        setRoleFlash({ id: role.id, ok: false });
        setTimeout(() => setRoleFlash(null), 1800);
      } else {
        setRoleFlash({ id: role.id, ok: true });
        setTimeout(() => setRoleFlash(null), 1000);
        onRoleChange();
      }
    } catch (e: any) {
      setRoleError(e?.message || 'Network error');
      setRoleFlash({ id: role.id, ok: false });
      setTimeout(() => setRoleFlash(null), 1800);
    } finally {
      setUpdatingRole(null);
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06] shrink-0">
        <h3 className="text-white font-semibold text-sm">Member Details</h3>
        <button onClick={onClose} className="p-1.5 text-gray-600 hover:text-white transition-colors rounded-lg hover:bg-white/[0.05]">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Profile */}
        <div className="flex flex-col items-center text-center gap-3 pb-4 border-b border-white/[0.05]">
          <Avatar src={member.avatar} name={member.displayName} size={72} />
          <div>
            <p className="text-white font-semibold text-base">{member.displayName}</p>
            {member.globalName && member.globalName !== member.displayName && (
              <p className="text-gray-500 text-xs mt-0.5">@{member.username}</p>
            )}
          </div>
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2 w-full text-center">
            <div className="bg-white/[0.03] rounded-xl p-2.5 border border-white/[0.04]">
              <p className="text-white font-bold text-lg">{member.level?.level ?? '—'}</p>
              <p className="text-gray-600 text-[10px]">Level</p>
            </div>
            <div className="bg-white/[0.03] rounded-xl p-2.5 border border-white/[0.04]">
              <p className="text-white font-bold text-lg">{member.pokemon.collectionCount}</p>
              <p className="text-gray-600 text-[10px]">Pokémon</p>
            </div>
            <div className="bg-white/[0.03] rounded-xl p-2.5 border border-white/[0.04]">
              <p className="text-white font-bold text-lg">{member.roles.length}</p>
              <p className="text-gray-600 text-[10px]">Roles</p>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="space-y-2 text-[12px]">
          <div className="flex items-center gap-2 text-gray-500">
            <Hash className="w-3.5 h-3.5 shrink-0" />
            <span className="text-gray-600">ID</span>
            <span className="text-gray-300 ml-auto font-mono">{member.id}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-500">
            <Clock className="w-3.5 h-3.5 shrink-0" />
            <span className="text-gray-600">Joined</span>
            <span className="text-gray-300 ml-auto">{relTime(member.joinedAt)}</span>
          </div>
          {member.pokemon.shinyHunt && (
            <div className="flex items-center gap-2">
              <Star className="w-3.5 h-3.5 text-yellow-400 shrink-0" />
              <span className="text-gray-600">Shiny Hunt</span>
              <span className="text-yellow-300 ml-auto capitalize">{member.pokemon.shinyHunt}</span>
            </div>
          )}
        </div>

        {/* Error banner */}
        {roleError && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-[11px]">
            <X className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-red-300 font-medium leading-tight">Role update failed</p>
              <p className="text-red-400/70 mt-0.5 break-words">{roleError}</p>
            </div>
            <button onClick={() => setRoleError(null)} className="text-red-500 hover:text-red-300 transition-colors shrink-0">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Current Roles */}
        {member.roles.length > 0 && (
          <div className="space-y-2">
            <p className="text-gray-600 text-[10px] font-semibold uppercase tracking-wider">Current Roles</p>
            <div className="flex flex-wrap gap-1.5">
              {member.roles.map((r, i) => <RoleBadge key={i} role={r} />)}
            </div>
          </div>
        )}

        {/* Manage Roles */}
        {allRoles.length > 0 && (
          <div className="space-y-2">
            <p className="text-gray-600 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1">
              <Shield className="w-3 h-3" /> Manage Roles
            </p>
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {allRoles.map((role: any) => {
                const hasRole = memberRoleNames.has(role.name);
                const isUpdating = updatingRole === role.id;
                const flash = roleFlash?.id === role.id ? roleFlash : null;
                const color = role.color || '#5865f2';
                return (
                  <button
                    key={role.id}
                    onClick={() => toggleRole(role)}
                    disabled={!!updatingRole}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all border text-[12px]
                      ${flash?.ok === true
                        ? 'border-green-500/40 bg-green-500/10'
                        : flash?.ok === false
                          ? 'border-red-500/40 bg-red-500/10'
                          : hasRole
                            ? 'border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.06]'
                            : 'border-transparent hover:bg-white/[0.03] hover:border-white/[0.04]'
                      } disabled:opacity-40`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                    <span className={`flex-1 truncate ${
                      flash?.ok === true ? 'text-green-300' :
                      flash?.ok === false ? 'text-red-300' :
                      hasRole ? 'text-white' : 'text-gray-500'
                    }`}>{role.name}</span>
                    {isUpdating
                      ? <Loader2 className="w-3.5 h-3.5 text-gray-500 animate-spin shrink-0" />
                      : flash?.ok === true
                        ? <CheckCircle className="w-3.5 h-3.5 text-green-400 shrink-0" />
                        : flash?.ok === false
                          ? <X className="w-3.5 h-3.5 text-red-400 shrink-0" />
                          : hasRole
                            ? <span className="text-[10px] text-gray-500 shrink-0">Remove</span>
                            : <span className="text-[10px] text-gray-700 shrink-0">Add</span>
                    }
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Main Members panel ─────────────────────────────────────────────────── */
export default function RolesPanel({ guildData, guild }: RolesPanelProps) {
  const { token } = useAuth();
  const guildId = guild?.id;

  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Member | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  // Fetch members
  useEffect(() => {
    if (!guildId || !token) {
      console.log('[Members] Skipping fetch — no guildId or token', { guildId, hasToken: !!token });
      return;
    }

    // Dev mode: inject mock members instead of calling real API
    if (token === 'dev-token' && import.meta.env.DEV) {
      const mockMembers: Member[] = [
        { id: '1', username: 'alice', globalName: 'Alice', displayName: 'Alice', avatar: '', joinedAt: new Date(Date.now() - 86400000 * 10).toISOString(), isOwner: true, roles: [{ name: 'Admin', color: '#e74c3c' }], pokemon: { collectionCount: 42, shinyHunt: 'Pikachu' }, level: { level: 15, xp: 3200 } },
        { id: '2', username: 'bob', globalName: 'Bob', displayName: 'Bob', avatar: '', joinedAt: new Date(Date.now() - 86400000 * 5).toISOString(), isOwner: false, roles: [{ name: 'Member', color: '#2ecc71' }], pokemon: { collectionCount: 7, shinyHunt: null }, level: { level: 3, xp: 450 } },
        { id: '3', username: 'charlie', globalName: 'Charlie', displayName: 'Charlie', avatar: '', joinedAt: new Date(Date.now() - 86400000 * 30).toISOString(), isOwner: false, roles: [{ name: 'Moderator', color: '#3498db' }, { name: 'Member', color: '#2ecc71' }], pokemon: { collectionCount: 0, shinyHunt: null }, level: { level: 8, xp: 1800 } },
        { id: '4', username: 'diana', globalName: 'Diana', displayName: 'Diana', avatar: '', joinedAt: null, isOwner: false, roles: [{ name: 'Muted', color: '#95a5a6' }], pokemon: { collectionCount: 120, shinyHunt: 'Eevee' }, level: null },
      ];
      setMembers(mockMembers);
      setLoading(false);
      return;
    }

    const apiUrl = `${(import.meta.env.VITE_API_URL || '')}/api/dashboard/${guildId}/members`;
    console.log('[Members] Fetching members...', { apiUrl, guildId });
    const t0 = Date.now();
    setLoading(true);
    setError(null);
    fetch(apiUrl, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        console.log(`[Members] Response: ${r.status} ${r.statusText} (${Date.now() - t0}ms)`);
        if (r.ok) return r.json();
        return r.json().then((e: any) => {
          console.error('[Members] Error response:', e);
          throw new Error(e.error || `HTTP ${r.status}`);
        });
      })
      .then((data: Member[]) => {
        console.log(`[Members] Loaded ${data.length} members`, { sample: data.slice(0, 3).map(m => ({ name: m.displayName, roles: m.roles.length, pokemon: m.pokemon.collectionCount })) });
        setMembers(data);
        setLoading(false);
      })
      .catch((e: Error) => {
        console.error('[Members] Fetch failed:', e.message);
        setError(e.message);
        setLoading(false);
      });
  }, [guildId, token, refreshKey]);

  // Update selected member from refreshed list
  useEffect(() => {
    if (selected) {
      const refreshed = members.find(m => m.id === selected.id);
      if (refreshed) setSelected(refreshed);
    }
  }, [members]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return members;
    return members.filter(m =>
      m.displayName.toLowerCase().includes(q) ||
      m.username.toLowerCase().includes(q) ||
      m.roles.some(r => r.name.toLowerCase().includes(q))
    );
  }, [members, search]);

  const onlineCount = guildData?.presenceCount ?? null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-display font-bold text-white">Members</h2>
          <p className="text-gray-500 text-sm mt-0.5">
            {loading ? 'Loading…' : `${members.length.toLocaleString()} members`}
            {onlineCount ? ` · ${onlineCount.toLocaleString()} online` : ''}
          </p>
        </div>
        <button
          onClick={() => setRefreshKey(k => k + 1)}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-medium text-gray-400 hover:text-white border border-white/[0.06] hover:border-white/[0.10] transition-all disabled:opacity-40"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: Users, label: 'Members', value: guildData?.memberCount?.toLocaleString() ?? (members.length || '—'), color: 'blue' },
          { icon: Sparkles, label: 'Online', value: onlineCount?.toLocaleString() ?? '—', color: 'green' },
          { icon: Shield, label: 'Roles', value: (guildData?.roles?.length ?? 0).toString(), color: 'purple' },
          { icon: Swords, label: 'Pokémon Users', value: members.filter(m => m.pokemon.collectionCount > 0).length.toString(), color: 'yellow' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-dark-800/60 border border-white/[0.05] rounded-2xl p-4">
            <div className="flex items-center gap-2.5">
              <div className={`p-2 rounded-xl bg-${color}-500/10 text-${color}-400`}>
                <Icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-white font-bold text-lg leading-tight">{value}</p>
                <p className="text-gray-600 text-[10px]">{label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main panel with optional detail drawer */}
      <div className="flex gap-4 min-h-[500px]">
        {/* Member list */}
        <div className={`flex-1 min-w-0 bg-dark-800/60 border border-white/[0.05] rounded-2xl flex flex-col transition-all duration-200`}>
          {/* Search bar */}
          <div className="px-4 py-3 border-b border-white/[0.04] shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search members, roles…"
                className="w-full pl-9 pr-8 py-2 bg-white/[0.03] border border-white/[0.05] rounded-xl text-white text-[13px] placeholder-gray-700 focus:outline-none focus:border-primary/40 transition-colors"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-600 hover:text-white transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* List body */}
          <div className="flex-1 overflow-y-auto divide-y divide-white/[0.03]">
            {loading ? (
              <div className="flex items-center justify-center h-full py-24">
                <div className="text-center space-y-3">
                  <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
                  <p className="text-gray-600 text-sm">Fetching members…</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-full py-20 text-center px-8 gap-3">
                <div className="p-3 rounded-2xl bg-red-500/10">
                  <X className="w-6 h-6 text-red-400" />
                </div>
                <p className="text-gray-400 text-sm font-medium">Failed to load members</p>
                <p className="text-gray-600 text-xs">{error}</p>
                <button onClick={() => setRefreshKey(k => k + 1)} className="text-primary text-xs hover:underline">Try again</button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-20 text-center gap-3">
                <Users className="w-8 h-8 text-gray-700 mx-auto" />
                <p className="text-gray-500 text-sm">{search ? 'No members match your search' : 'No members found'}</p>
              </div>
            ) : filtered.map(member => {
              const isSelected = selected?.id === member.id;
              const topRole = member.roles[0];
              return (
                <button
                  key={member.id}
                  onClick={() => setSelected(isSelected ? null : member)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all group
                    ${isSelected ? 'bg-primary/5 border-l-2 border-primary' : 'hover:bg-white/[0.02] border-l-2 border-transparent'}`}
                >
                  <Avatar src={member.avatar} name={member.displayName} size={36} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-white text-[13px] font-medium truncate">{member.displayName}</span>
                      {member.pokemon.shinyHunt && (
                        <span title={`Shiny hunting ${member.pokemon.shinyHunt}`}><Star className="w-3 h-3 text-yellow-400 shrink-0" /></span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {topRole && (
                        <span className="text-[10px] truncate" style={{ color: topRole.color || '#5865f2' }}>
                          {topRole.name}
                        </span>
                      )}
                      {member.roles.length > 1 && (
                        <span className="text-gray-700 text-[10px]">+{member.roles.length - 1}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {member.pokemon.collectionCount > 0 && (
                      <span className="text-[10px] text-gray-600 hidden sm:block">{member.pokemon.collectionCount} pkm</span>
                    )}
                    {member.level && (
                      <span className="text-[10px] text-gray-600 hidden sm:block">Lv.{member.level.level}</span>
                    )}
                    <ChevronRight className={`w-3.5 h-3.5 transition-all ${isSelected ? 'text-primary rotate-90' : 'text-gray-700 group-hover:text-gray-500'}`} />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer */}
          {!loading && !error && (
            <div className="px-4 py-2.5 border-t border-white/[0.04] shrink-0">
              <p className="text-gray-700 text-[11px]">
                {filtered.length !== members.length
                  ? `${filtered.length} of ${members.length} members`
                  : `${members.length} members`}
              </p>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="w-72 shrink-0 bg-dark-800/60 border border-white/[0.05] rounded-2xl overflow-hidden hidden lg:flex lg:flex-col">
            <MemberDetail
              member={selected}
              guildData={guildData}
              token={token!}
              guildId={guildId}
              onClose={() => setSelected(null)}
              onRoleChange={() => setRefreshKey(k => k + 1)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
